import { useCallback, useEffect, useState } from 'react';
import { List, message, Modal, Typography, Upload } from 'antd';
import { Paragraph, Title } from '@/components/atoms';
import api from '@/api';
import { pcommon, ptax } from '@/proto/proto';
import queryString from 'query-string';
import {
  API_TAX_CONTRACT_UPLOAD,
  API_TAX_NOTICE,
  API_TAX_NOTICE_EXCEPT,
  API_TAX_NOTICE_REMAIN_LIST,
  FORMAT_DATE_GENERAL_EXCEPT_TIME,
  FORMAT_DATE_SHORTEN_KR,
  IMAGE_PATH_TAX_NOTICE,
  PATH_TAX_NOTICE_DETAIL,
  PATH_TAX_NOTICE_LIST,
  PATH_TAX_NOTICE_UPLOAD,
} from '@/constants/constant';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from '@/rootReducer';
import TaxNoticeListItem from '@/components/organisms/tax/notice/list/TaxNoticeListItem';
import TaxNoticeListFilter, { FilterDateRange } from '@/components/organisms/tax/notice/list/TaxNoticeListFilter';
import TaxTemplate from '@/components/template/tax/TaxTemplate';
import { writeFile } from '@/utils/excelExtension';
import moment from 'moment';
import { makePdfToFile, readBytes, resize } from '@/utils/fileExtension';
import { UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/es/upload/interface';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import TaxChangeRequestMethod, { apiOf } from '@/components/covalent/tax/change/TaxChangeRequestMethod';
import { ErrorMessage } from '@/constants/message';
import TaxNoticeStatus from '@/components/covalent/tax/notice/TaxNoticeStatus';
import TaxChangeInformationModal from '@/components/organisms/tax/change/TaxChangeInformationModal';
import TaxChangeRequestModal from '@/components/organisms/tax/change/TaxChangeRequestModal';

type PreviewStateType = {
  visible: boolean;
  title: string;
  image: string;
};

type CustomSendModalStateType = {
  taxNotices?: ptax.IPbTaxNotice[];
  visible: boolean;
};

type ContractUploadStateType = {
  taxNotice?: ptax.IPbTaxNotice;
  images: UploadFile[];
  visible: boolean;
};

type ConfirmModalStateType = {
  visible: boolean;
};

type ChangeInformationModalStateType = {
  visible: boolean;
  taxNotice?: ptax.IPbTaxNotice;
};

export type FilterStateType = pcommon.IPbPageable & {
  keyword?: string;
  date?: number;
  from?: string;
  to?: string;
  status?: number;
  type?: string;
};

const TaxNoticeListPage = () => {
  const history = useHistory();
  const { search } = useLocation();
  const { current, total, page, size, keyword: q, date, from, to, status, type } = queryString.parse(search);
  const { current: company } = useSelector((state: RootState) => state.company);
  const [isDownloadable, setDownloadable] = useState(false);

  const [loading, setLoading] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [data, setData] = useState<ptax.IPbTaxNoticeList>();
  const [remainList, setRemainList] = useState<ptax.IPbTaxNoticeList>();
  const [count, setCount] = useState(0);

  const [error, setError] = useState<string | undefined>(undefined);

  const [filter, setFilter] = useState<FilterStateType>({
    current: Number(current ?? 1),
    total: Number(total ?? 10),
    page: Number(page ?? 0),
    size: Number(size ?? 10),
    date: Number(date ?? FilterDateRange.WHOLE),
    status: Number(status ?? TaxNoticeStatus.ALL),
    keyword: String(q ?? ''),
  });

  const [customSendState, setCustomSendState] = useState<CustomSendModalStateType>({
    visible: false,
  });

  const [previewState, setPreviewState] = useState<PreviewStateType>({
    visible: false,
    title: '',
    image: '',
  });

  const [contractUploadState, setContractUploadState] = useState<ContractUploadStateType>({
    taxNotice: undefined,
    images: [],
    visible: false,
  });

  const [confirmState, setConfirmState] = useState<ConfirmModalStateType>({
    visible: false,
  });

  const [changeInformationState, setChangeInformationState] = useState<ChangeInformationModalStateType>({
    visible: false,
    taxNotice: undefined,
  });

  const getTaxNoticeList = useCallback(
    (companyId) => {
      setLoading(true);
      api
        .get(API_TAX_NOTICE, {
          params: {
            ...filter,
            companyId,
          },
        })
        .then((response) => {
          setData(response.data);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [filter],
  );

  const getRemainList = useCallback(
    (companyId) => {
      setLoading(true);
      api
        .get(API_TAX_NOTICE_REMAIN_LIST, {
          params: {
            ...filter,
            companyId,
          },
        })
        .then((response) => {
          setRemainList(response.data);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [filter],
  );

  const fetch = useCallback(() => {
    if (company) {
      getTaxNoticeList(company.base?.id);
      getRemainList(company.base?.id);
    }
  }, [company, getRemainList, getTaxNoticeList]);

  const download = useCallback(() => {
    api
      .get(API_TAX_NOTICE, {
        params: {
          ...filter,
          size: 10000,
          companyId: company.base?.id,
        },
      })
      .then((response) => {
        writeFile(
          response.data?.taxNotice?.map((tax: ptax.IPbTaxNotice) => ({
            fileName: tax.fileOrgName,
            sender: tax.sender,
            plateNo: tax.plateNo,
            date: moment(tax.violation?.first()?.violationAt).format(FORMAT_DATE_SHORTEN_KR),
            location: tax.violation?.first()?.location,
            details: tax.violation?.first()?.details,
          })) ?? [],
          ['파일이름', '발급기관', '차량번호', '위반일시', '위반장소', '위반내용'],
        );
      });
  }, [company, filter]);

  const onClickUploadContract = async () => {
    setContractUploadState({ ...contractUploadState, visible: false });
    if (contractUploadState.images.length <= 0) return;
    setLoading(true);
    const formData = new FormData();
    const files: any[] = [];
    formData.append('taxNoticeIdList', `${contractUploadState.taxNotice?.base?.id}`);

    const images = contractUploadState.images.map((image) => image).filter((image): image is UploadFile => image !== undefined);
    // eslint-disable-next-line no-restricted-syntax
    for (const image of images) {
      console.log(image.originFileObj?.type);
      // eslint-disable-next-line no-await-in-loop
      const imageData = await readBytes(image);
      console.log(imageData);
      files.push({ type: 'contract', data: imageData });
    }
    api
      .post(API_TAX_CONTRACT_UPLOAD, 'ptax.PbTaxContractUploadRequest', {
        taxNoticeId: contractUploadState.taxNotice?.base?.id,
        file: files,
      })
      .then(() => {
        message.success('계약서 업로드에 성공했습니다.');
        fetch();
      })
      .catch(({ response }) => {
        setError(response?.data ?? ErrorMessage.network.disconnected);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const send = useCallback(
    async (taxNoticeList: ptax.IPbTaxNotice[], method: TaxChangeRequestMethod, contactId?: number) => {
      setLoading(true);

      const { id } = company.base;

      const formData = new FormData();

      formData.append('companyId', id);

      if (contactId) {
        formData.append('contactId', `${contactId}`);
      }

      await taxNoticeList.map(async (item, index) => {
        formData.append('taxNoticeIdList', `${item.base?.id}`);
        await makePdfToFile(item, company, `document${index}.pdf`).then((pdf) => {
          formData.append('files', pdf);
        });
      });

      api
        .upload(apiOf(method), formData)
        .then(() => {
          message.success(`${taxNoticeList.length}건의 민원을 처리했습니다.`);
          fetch();
        })
        .catch(({ response }) => {
          setError(response?.data ?? ErrorMessage.network.disconnected);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [company, fetch],
  );

  const except = useCallback(
    (taxNoticeList: ptax.IPbTaxNotice[], label?: string) => {
      setLoading(true);

      const { id } = company.base;

      const formData = new FormData();

      formData.append('companyId', id);

      taxNoticeList.forEach((taxNotice) => {
        formData.append('taxNoticeIdList', `${taxNotice.base?.id}`);
      });

      if (label) {
        formData.append('label', label);
      }

      api
        .upload(API_TAX_NOTICE_EXCEPT, formData)
        .then(() => {
          message.success(`${taxNoticeList.length}건의 민원을 개별 처리했습니다.`);
          fetch();
        })
        .catch(({ response }) => {
          setError(response?.data);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [company, fetch],
  );

  useEffect(() => {
    setCount(remainList?.taxNotice?.length ?? 0);
  }, [remainList]);

  useEffect(() => {
    fetch();
  }, [company, fetch]);

  useEffect(() => {
    const params = Object.entries(filter).map(([key, value]) => `${key}=${value}`);
    history.push(`${PATH_TAX_NOTICE_LIST}?${params.join('&')}`);
  }, [filter, history]);

  useEffect(() => {
    if (error) {
      message.error(error);
      setError(undefined);
    }
  }, [error]);

  useEffect(() => {
    const isDataExist = (data?.taxNotice?.length ?? 0) > 0;
    const isDateRangeExist = (filter.from?.length ?? 0) > 0 && (filter.to?.length ?? 0) > 0;
    const diffMonths = moment(filter.from, FORMAT_DATE_GENERAL_EXCEPT_TIME).diff(moment(filter.to, FORMAT_DATE_GENERAL_EXCEPT_TIME), 'months');
    const isDateRangeIn = diffMonths <= 3;
    setDownloadable(isDataExist && isDateRangeExist && isDateRangeIn);
  }, [data, filter.from, filter.to]);

  return (
    <TaxTemplate>
      <Title>고지서 목록</Title>
      <TaxNoticeListFilter
        filter={filter}
        downloadable={isDownloadable}
        count={count}
        onSearch={(value) => {
          setFilter({ ...filter, keyword: value.replace(' ', '+'), page: 0 });
        }}
        onChangeSearch={(value) => {
          setSearchKeyword(value);
        }}
        onChangeDateRange={(first, second, third) => {
          setFilter({ ...filter, from: first, to: second, date: third });
        }}
        onChangeFilter={(value) => {
          setFilter({ ...filter, status: value, page: 0 });
        }}
        onClickDownload={() => {
          download();
        }}
        onClickUpload={() => history.push(PATH_TAX_NOTICE_UPLOAD)}
        onClickSendAuto={() => setConfirmState({ visible: true })}
      />
      <List
        rowKey={(item: ptax.IPbTaxNotice) => `${item.base?.id}`}
        dataSource={data?.taxNotice ?? []}
        loading={loading}
        pagination={{
          current: Number(data?.pageable?.current ?? 0) + 1,
          total: Number(data?.pageable?.total ?? 1),
          // eslint-disable-next-line no-shadow
          onChange: (page, size) => {
            setFilter({ ...filter, page: page - 1, size });
          },
          showSizeChanger: true,
          className: 'pagination',
          size: 'small',
          position: 'bottom',
        }}
        renderItem={(item: ptax.IPbTaxNotice) => {
          return (
            <TaxNoticeListItem
              data={item}
              loading={loading}
              company={company}
              keyword={searchKeyword}
              onClickImage={() =>
                setPreviewState({
                  visible: true,
                  image: IMAGE_PATH_TAX_NOTICE(company.base?.id, item),
                  title: '',
                })
              }
              onClickSend={() =>
                setCustomSendState({
                  ...customSendState,
                  visible: true,
                  taxNotices: [item],
                })
              }
              onClickUploadContract={() =>
                setContractUploadState({
                  images: [],
                  visible: true,
                  taxNotice: item,
                })
              }
              onClickDetail={() => {
                history.push(PATH_TAX_NOTICE_DETAIL(item));
              }}
              onClickRegisterContact={() => {
                setLoading(true);
                api
                  .post('/tax-notice/contact/register/request', 'ptax.PbTaxNotice', item)
                  .then(() => {
                    message.success('기관 등록 요청이 완료되었습니다.');
                  })
                  .finally(() => {
                    setLoading(false);
                  });
              }}
              onClickChangeInformation={() => setChangeInformationState({ taxNotice: item, visible: true })}
            />
          );
        }}
      />
      <Modal
        visible={contractUploadState.visible}
        title="계약서 업로드"
        cancelText="취소"
        okText="등록"
        onCancel={() => setContractUploadState({ ...contractUploadState, visible: false })}
        onOk={onClickUploadContract}
      >
        <Upload.Dragger
          name="file"
          accept=".jpg, .jpeg, .png, .pdf"
          style={{ marginBottom: contractUploadState.images.length > 0 ? 20 : 0 }}
          customRequest={(option) => {
            const { onSuccess } = option;
            if (onSuccess) {
              onSuccess('done', new XMLHttpRequest());
            }
          }}
          transformFile={resize}
          listType="picture"
          fileList={contractUploadState.images}
          onChange={({ fileList }: UploadChangeParam<UploadFile>) => {
            setContractUploadState({ ...contractUploadState, images: fileList.slice(-1) });
          }}
        >
          <Typography className="upload-message">여기로 파일을 끌어 넣거나 클릭해 추가하세요.</Typography>
        </Upload.Dragger>
      </Modal>
      <Modal
        visible={previewState.visible}
        title={previewState.title}
        footer={null}
        onCancel={() => setPreviewState({ ...previewState, visible: false })}
      >
        <LazyLoadImage alt="preview" style={{ width: '100%' }} src={previewState.image} />
      </Modal>
      <TaxChangeRequestModal
        allowExcept
        title="민원 등록"
        visible={customSendState.visible}
        onCancel={() => setCustomSendState({ ...customSendState, visible: false })}
        onSend={send}
        onExcept={except}
        company={company}
        taxNotices={customSendState.taxNotices}
      />
      <Modal
        visible={confirmState.visible}
        title="자동 민원신청 확인"
        onCancel={() => setConfirmState({ ...confirmState, visible: false })}
        onOk={() => {
          setConfirmState({ ...confirmState, visible: false });
          send(remainList?.taxNotice ?? [], TaxChangeRequestMethod.AUTO);
        }}
      >
        <Paragraph>자동으로 처리 가능한 {count}건의 민원이 신청됩니다.</Paragraph>
      </Modal>
      <TaxChangeInformationModal
        taxNotice={changeInformationState.taxNotice}
        company={company}
        visible={changeInformationState.visible}
        onCancel={() => setChangeInformationState({ ...changeInformationState, visible: false })}
      />
    </TaxTemplate>
  );
};

export default TaxNoticeListPage;
